import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Build
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AdminLayout from '../../components/AdminLayout';
import { getAllServices, createService, updateService, deleteService, resolveImageUrl } from '../../utils/api';

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const ManageServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: null,
    iconPreview: null,
    features: []
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load services from API
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await getAllServices();
      if (response.data && response.data.success) {
        setServices(response.data.services || []);
      } else {
        showSnackbar('Failed to load services', 'error');
        setServices([]);
      }
    } catch (error) {
      showSnackbar('Failed to load services', 'error');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        title: service.title || '',
        description: service.description || '',
        icon: null,
        iconPreview: service.icon_url ? resolveImageUrl(service.icon_url) : null,
        features: service.features || []
      });
    } else {
      setEditingService(null);
      setFormData({
        title: '',
        description: '',
        icon: null,
        iconPreview: null,
        features: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: null,
      iconPreview: null,
      features: []
    });
  };

  const handleSubmit = async () => {
    try {
      // Validation: require icon, title, description for create
      if (!editingService && (!formData.icon || !formData.title || !formData.description)) {
        showSnackbar('Please provide service title, description, and upload an icon.', 'error');
        return;
      }
      if (editingService) {
        // Update service
        const response = await updateService(editingService.id, formData);
        if (response.data && response.data.success) {
          showSnackbar('Service updated successfully!');
          loadServices(); // Reload services from API
        } else {
          showSnackbar('Failed to update service', 'error');
        }
      } else {
        // Create new service
        const response = await createService(formData);
        if (response.data && response.data.success) {
          showSnackbar('Service created successfully!');
          loadServices(); // Reload services from API
        } else {
          showSnackbar('Failed to create service', 'error');
        }
      }
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Failed to save service', 'error');
    }
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const response = await deleteService(serviceId);
        if (response.data && response.data.success) {
          showSnackbar('Service deleted successfully!');
          loadServices(); // Reload services from API
        } else {
          showSnackbar('Failed to delete service', 'error');
        }
      } catch (error) {
        showSnackbar('Failed to delete service', 'error');
      }
    }
  };

  const handleIconChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        showSnackbar('Invalid file type. Only JPEG, PNG, GIF, WebP, and SVG are allowed.', 'error');
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showSnackbar('File size too large. Maximum 5MB allowed.', 'error');
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      setFormData({
        ...formData,
        icon: file,
        iconPreview: previewUrl
      });
    }
  };

  return (
    <AdminLayout title="Manage Services">
      <Container maxWidth="lg">
        {/* Header Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{
              fontWeight: 600,
              fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' }
            }}>
              Services Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Services: {services.length}
            </Typography>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>

            <Button
              variant="contained"
              startIcon={
                <Add
                  sx={{
                    fontSize: { xs: 16, sm: 20, md: 24 }  // px values for different breakpoints
                  }}
                />
              }
              onClick={() => handleOpenDialog()}
              sx={{
                fontSize: { xs: '0.5rem', sm: '0.9rem', md: '1rem' }
              }}
            >
              Add Service
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          /* Services Grid */
          <Grid container spacing={3}>
            {services.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service.id}>
                <ServiceCard>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Icon and Title on same line */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {service.icon_url && (
                        <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <img
                            src={resolveImageUrl(service.icon_url)}
                            alt={`${service.title} icon`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              display: 'block'
                            }}
                          />
                        </Box>
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {service.title}
                      </Typography>
                    </Box>

                    {/* Features */}
                    {service.features && service.features.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                          Features:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {service.features.slice(0, 4).map((feature, index) => (
                            <Chip
                              key={index}
                              label={feature}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{
                                borderRadius: 3,
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          ))}
                          {service.features.length > 4 && (
                            <Chip
                              label={`+${service.features.length - 4} more`}
                              size="small"
                              variant="filled"
                              color="secondary"
                              sx={{
                                borderRadius: 3,
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary">
                      {service.description.slice(0, 180) + "..."}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleOpenDialog(service)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(service.id)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingService ? 'Edit Service' : 'Add New Service'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Service Icon
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="icon-upload"
                    type="file"
                    onChange={handleIconChange}
                  />
                  <label htmlFor="icon-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      sx={{ mb: 2 }}
                    >
                      {formData.icon ? 'Change Icon' : 'Upload Icon'}
                    </Button>
                  </label>

                  {formData.iconPreview && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Icon Preview:
                      </Typography>
                      <Box sx={{
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1
                      }}>
                        <img
                          src={formData.iconPreview}
                          alt="Icon preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.features}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, features: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Features"
                      placeholder="Type and press Enter to add features"
                      helperText="Add features one by one by typing and pressing Enter"
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={editingService ? false : (!formData.title || !formData.description || !formData.icon)}
            >
              {editingService ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminLayout>
  );
};

export default ManageServices;
